// Typed wrappers for /v1/admin/payout-csv-formats (migration 043).
//
// SUPERADMIN ONLY on the backend (auth.RequireSuperadmin, which refuses plain
// admins). The panel hides the screen; the server is the authority.
//
// A format is the layout of the CSV an operator uploads to a bank that has no
// payout API. Getting it wrong means telling a bank to pay the wrong amount to
// the right person, or the right amount to nobody — so the field catalogue,
// the renderings and the validation all live on the SERVER and are fetched
// from it. Duplicating that list here is how a builder ends up offering a
// column the renderer does not know about.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient.
import { apiClient } from "./client";

/** One rendering option for a field, as advertised by the backend. */
export interface FieldRendering {
  value: string;
  default: boolean;
}

/**
 * FieldSpec — one column source an admin can pick.
 *
 * `kind` drives which renderings apply: amounts get rupee/paise choices,
 * modes get IMPS/NEFT vocabularies, dates get layouts, text gets none.
 */
export interface FieldSpec {
  source: string;
  label: string;
  kind: "text" | "amount" | "mode" | "date" | "number";
  help: string;
  renderings?: FieldRendering[];
}

/** One column of a layout. `position` is 1-based and is the emission order. */
export interface FormatColumn {
  position: number;
  header: string;
  source: string;
  rendering: string;
  constant: string;
}

export interface CsvFormat {
  id: number;
  name: string;
  description: string;
  delimiter: string;
  include_header: boolean;
  bank_account_id?: number | null;
  created_at: string;
  updated_at: string;
  columns?: FormatColumn[];
  column_count?: number;
}

export interface CsvFormatDraft {
  name: string;
  description: string;
  delimiter: string;
  include_header: boolean;
  bank_account_id: number | null;
  columns: FormatColumn[];
}

/** The builder's palette. Served by the backend so the two cannot drift. */
export async function getFields(): Promise<FieldSpec[]> {
  const r = await apiClient.get<{ fields: FieldSpec[] }>(
    "/v1/admin/payout-csv-formats/fields",
  );
  return r.data.fields ?? [];
}

export async function listFormats(): Promise<CsvFormat[]> {
  const r = await apiClient.get<{ formats: CsvFormat[] }>(
    "/v1/admin/payout-csv-formats",
  );
  return r.data.formats ?? [];
}

export async function getFormat(id: number): Promise<CsvFormat> {
  const r = await apiClient.get<{ format: CsvFormat }>(
    `/v1/admin/payout-csv-formats/${id}`,
  );
  return r.data.format;
}

export async function createFormat(draft: CsvFormatDraft): Promise<CsvFormat> {
  const r = await apiClient.post<{ format: CsvFormat }>(
    "/v1/admin/payout-csv-formats",
    draft,
  );
  return r.data.format;
}

export async function updateFormat(
  id: number,
  draft: CsvFormatDraft,
): Promise<void> {
  await apiClient.put(`/v1/admin/payout-csv-formats/${id}`, draft);
}

export async function deleteFormat(id: number): Promise<void> {
  await apiClient.delete(`/v1/admin/payout-csv-formats/${id}`);
}

/**
 * previewFormat renders an UNSAVED draft against sample rows.
 *
 * The backend renders it through the exact code path the real export uses. A
 * preview computed in the browser would be a preview of the browser's idea of
 * the format, which is the one thing it must not be.
 */
export async function previewFormat(draft: CsvFormatDraft): Promise<string> {
  const r = await apiClient.post<{ csv: string }>(
    "/v1/admin/payout-csv-formats/preview",
    draft,
  );
  return r.data.csv ?? "";
}

/** defaultRendering picks the backend-advertised default for a field. */
export function defaultRendering(spec: FieldSpec | undefined): string {
  if (!spec?.renderings?.length) return "";
  return spec.renderings.find((r) => r.default)?.value ?? spec.renderings[0].value;
}

/** A starter layout, so a new format is not an empty screen. */
export function starterColumns(): FormatColumn[] {
  return [
    { position: 1, header: "Sr No", source: "row_number", rendering: "", constant: "" },
    { position: 2, header: "Beneficiary Name", source: "beneficiary_name", rendering: "", constant: "" },
    { position: 3, header: "Account Number", source: "beneficiary_account_no", rendering: "", constant: "" },
    { position: 4, header: "IFSC", source: "beneficiary_ifsc", rendering: "", constant: "" },
    { position: 5, header: "Amount", source: "amount", rendering: "rupees_2dp", constant: "" },
    { position: 6, header: "Mode", source: "txn_mode", rendering: "imps_neft", constant: "" },
  ];
}
